import { LitState } from 'lit-element-state'
import 'urlpattern-polyfill'
import { extractRoutes } from './utils.js'
import store from '../store/index.js'
import { handleize } from '../../lib/utils'

import ROUTES from './routes.js'

/**
 * Router state
 * @extends LitState
 */
class RouterState extends LitState {
  /**
   * @returns {Object} state variables
   * @property {string} base - base path for the router
   * @property {string} path - current path
   * @property {Object[]} routes - all routes
   * @property {string} routes[].path - route path
   * @property {string} routes[].name - route name
   * @property {function} routes[].render - route render function
   * @property {Object[]} routes[].children - route children
   * @property {URLPattern} routes[].pattern - route pattern (uses URLPattern)
   * @property {boolean} isReady - is the router view ready
   */
  static get stateVars() {
    return {
      base: '',
      path: undefined,
      routes: extractRoutes(ROUTES),
      isReady: false,
    }
  }

  /**
   * Matches route by path
   *
   * @param {string} path - path to match
   * @returns {Object} route
   */
  matchRoute(path) {
    const privateUrl = this.getPathInternalPrivateUrl(path);
    return typeof path === 'undefined'
      ? undefined
      : this.routes.filter(r => r.name != "404").find(r => r.pattern.test(privateUrl)) || this.routes.find((r) => r.name === '404')
  }

  /**
   * Gets route by path
   *
   * @param {string} path - path to match
   * @returns {Object} route
   */
  matchAndHydrateRoute(path) {
    const route = this.matchRoute(path)
    if (!route) return

    return this.hydrateRoute(route, path)
  }

  /**
   * Hydrates a route with params and standardised computed path
   * @param {Object} route - route to hydrate
   * @returns {Object} route
   */
  hydrateRoute(route, path) {
    const params =
      route?.pattern.exec(this.getPathInternalPrivateUrl(path)).pathname
        .groups || {}
    const sanitizedPath = Object.keys(params).reduce(
      (path, key) => path.replace(`:${key}`, params[key]),
      `${route.path}`
    )

    return {
      ...{},
      ...route,
      params,
      sanitizedPath,
    }
  }

  /**
   * Returns the internal url for a given path
   * This URL is not the same as the public URL. This url only serves to match the route using the URLPattern
   */
  getPathInternalPrivateUrl(path) {
    return `${window.location.origin}${path}`
  }

  /**
   * Returns the internal url for a given path
   */
  get internalPrivateUrl() {
    return this.getPathInternalPrivateUrl(this.path)
  }

  /**
   * Returns the current route
   * @returns {Object} route
   * @property {string} route.path - route path
   * @property {string} route.name - route name
   * @property {function} route.render - route render function
   * @property {Object[]} route.children - route children
   * @property {URLPattern} route.pattern - route pattern (uses URLPattern)
   */
  get current() {
    return this.matchAndHydrateRoute(this.path) ?? {}
  }

  /**
   * Returns the quiz questions with their assigned paths
   * @returns {Array} quiz questions with paths
   */
  get quizQuestionsWithPaths() {
    return store.quizCategories.reduce(
      (paths, category) => [
        ...paths,
        ...category.questions.map((question) => ({
          path: `/${handleize(category.handle)}/${handleize(question.handle)}`,
          question,
        })),
      ],
      []
    )
  }

  /**
   * Returns the linear navigation paths ordered in an array
   * @returns {Array} linear navigation paths ordered
   * 1. Intro
   * 2. Questions (many)
   * 3. Summary
   */
  get linearNavigationPaths() {
    return ['/', ...this.quizQuestionsWithPaths.map((q) => q.path), '/summary']
  }

  /**
   * Returns the a relative path based on an offest in the linear navigation
   *
   * @param {string} path - path to get the relative path from
   * @param {number} offset - offset to get the relative path from
   */
  getLinearRelativePath(path, offset) {
    if (this.linearNavigationPaths.indexOf(path) < 0) return undefined

    return this.linearNavigationPaths[
      this.linearNavigationPaths.indexOf(path) + offset
    ]
  }

  /**
   * Returns the previous path in the linear navigation
   * @returns {string} previous path
   */
  get previousPath() {
    return this.getLinearRelativePath(this.current?.sanitizedPath, -1)
  }

  /**
   * Returns the next path in the linear navigation
   * @returns {string} next path
   */
  get nextPath() {
    return this.getLinearRelativePath(this.current?.sanitizedPath, +1)
  }

  /**
   * Returns the last available path in the linear navigation
   * Availability is determined based on user input/progress
   * @returns {string} last available path
   */
  get lastAvailablePath() {
    if (!store.customerIsCompleted) return '/'
    
    const enabledPaths = this.quizQuestionsWithPaths
      .filter((q) => q.question.isEnabled)
      .map((q) => q.path)

    return enabledPaths[enabledPaths.length - 1]
  }

  next() {
    this.path = this.nextPath
  }

  go(path) {
    this.path = path
  }
}

const routerState = new RouterState()

/**
 * Router observer
 * updates the window location hash when the path changes
 */
routerState.addObserver((name) => {
  switch (name) {
    case 'path':
      if (store.isStatic) return;
      window.location.hash = routerState.path
  }
})

export default routerState
